import type { OrderInfo } from "@jasper/client-models";
import { useTranslation } from "react-i18next";
import { Avatar, Group, Select, Stack } from "@mantine/core";

import type { FieldValueProps } from "../../types";
import { PatientForm } from "../patient";

export type DentistOption = { value: string; label: string; name: string };

type Props = FieldValueProps<OrderInfo> & {
  dentistOptions: DentistOption[];
  onDentistSearchChange?: (value: string) => void;
  isDentistsLoading?: boolean;
};

export const OrderInfoForm = ({
  value,
  errors,
  onChange,
  onBlur,
  dentistOptions,
  onDentistSearchChange,
  isDentistsLoading = false,
}: Props) => {
  const { t } = useTranslation("forms");

  return (
    <Stack gap="md">
      <Select
        label={t("forms.orderInfo.labels.dentist")}
        placeholder={t("forms.orderInfo.placeholders.dentist")}
        data={dentistOptions.map(option => ({
          label: option.label,
          value: option.value,
        }))}
        renderOption={({ option }) => {
          const dentist = dentistOptions.find(
            dentist => dentist.value === option.value
          );
          return (
            <Group>
              <Avatar name={dentist?.name} />
              {option.label}
            </Group>
          );
        }}
        value={value?.dentistId ?? null}
        onChange={v => onChange({ ...value, dentistId: v ?? undefined })}
        onBlur={() => onBlur?.()}
        searchable
        onSearchChange={onDentistSearchChange}
        clearable
        nothingFoundMessage={
          isDentistsLoading
            ? t("forms.orderInfo.searching")
            : t("forms.orderInfo.noDentistFound")
        }
        filter={({ options }) => options}
        error={errors?.dentistId ? t("common.toProvide") : undefined}
      />

      <PatientForm
        value={value?.patient}
        errors={errors?.patient}
        onChange={patient => onChange({ ...value, patient })}
        onBlur={() => onBlur?.()}
      />
    </Stack>
  );
};
